import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Minus, Plus, X } from "lucide-react"

/**
 * One image, sized against the width of the screen.
 *
 * It opens at 80% of the viewport's width - scaled up as well as down, so
 * detail in a drawing or a screenshot is readable straight away - and zooms
 * from there with the buttons, the + / - / 0 keys, or Ctrl (Cmd) and the
 * wheel. Anything taller or wider than the screen scrolls rather than
 * shrinking. Clicking the backdrop closes the view; clicking the picture
 * itself does nothing, so a stray click while looking does not throw it away.
 * Escape closes too.
 */

const DEFAULT_ZOOM = 0.8
const ZOOMS = [0.25, 0.5, 0.65, 0.8, 1, 1.25, 1.5, 2, 3, 4]
const GUTTER = 32 // px-4 either side

function step(zoom, direction) {
	if (direction > 0) return ZOOMS.find((z) => z > zoom + 0.001) ?? zoom
	return [...ZOOMS].reverse().find((z) => z < zoom - 0.001) ?? zoom
}

export function ImageLightbox({ src, filename, onClose }) {
	const [zoom, setZoom] = useState(DEFAULT_ZOOM)
	const [viewport, setViewport] = useState(() => window.innerWidth)
	const overlayRef = useRef(null)

	useEffect(() => {
		setZoom(DEFAULT_ZOOM)
	}, [src])

	useEffect(() => {
		if (!src) return undefined
		const onKey = (event) => {
			if (event.key === "Escape") onClose()
			else if (event.key === "+" || event.key === "=") setZoom((z) => step(z, 1))
			else if (event.key === "-" || event.key === "_") setZoom((z) => step(z, -1))
			else if (event.key === "0") setZoom(DEFAULT_ZOOM)
		}
		const onResize = () => setViewport(window.innerWidth)
		const overflow = document.body.style.overflow
		document.body.style.overflow = "hidden"
		window.addEventListener("keydown", onKey)
		window.addEventListener("resize", onResize)
		return () => {
			document.body.style.overflow = overflow
			window.removeEventListener("keydown", onKey)
			window.removeEventListener("resize", onResize)
		}
	}, [src, onClose])

	// Ctrl/Cmd + wheel (and a trackpad pinch, which arrives as one) zooms the
	// picture rather than the page. Registered by hand: React's wheel handler
	// is passive and cannot cancel the browser's own zoom.
	useEffect(() => {
		const overlay = overlayRef.current
		if (!overlay) return undefined
		const onWheel = (event) => {
			if (!event.ctrlKey && !event.metaKey) return
			event.preventDefault()
			setZoom((z) => step(z, event.deltaY < 0 ? 1 : -1))
		}
		overlay.addEventListener("wheel", onWheel, { passive: false })
		return () => overlay.removeEventListener("wheel", onWheel)
	}, [src])

	if (!src) return null
	const width = Math.max(1, viewport - GUTTER) * zoom

	return createPortal(
		<div
			ref={overlayRef}
			className="keep-light fixed inset-0 z-[60] cursor-zoom-out overflow-auto bg-ink-950/85"
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-label={filename || "Image"}
		>
			<div
				// Fixed, so the controls stay in reach while a large image scrolls under them.
				className="fixed inset-x-0 top-0 z-10 flex items-center gap-2 bg-ink-950/60 px-4 py-3 backdrop-blur-sm"
				onClick={(event) => event.stopPropagation()}
			>
				{filename && (
					<span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-white/85">
						{filename}
					</span>
				)}
				<span className="ml-auto flex shrink-0 items-center gap-1">
					<LightboxButton
						label="Zoom out"
						onClick={() => setZoom((z) => step(z, -1))}
						disabled={zoom <= ZOOMS[0]}
					>
						<Minus size={16} aria-hidden />
					</LightboxButton>
					<button
						type="button"
						onClick={() => setZoom(DEFAULT_ZOOM)}
						className="h-8 min-w-[3.5rem] rounded-lg px-2 text-[0.8125rem] font-medium tabular text-white/85 transition-colors hover:bg-white/10 hover:text-white"
						title="Reset zoom"
						aria-label={`Zoom ${Math.round(zoom * 100)}%, reset`}
					>
						{Math.round(zoom * 100)}%
					</button>
					<LightboxButton
						label="Zoom in"
						onClick={() => setZoom((z) => step(z, 1))}
						disabled={zoom >= ZOOMS[ZOOMS.length - 1]}
					>
						<Plus size={16} aria-hidden />
					</LightboxButton>
					<span className="mx-1 h-5 w-px bg-white/20" aria-hidden />
					<LightboxButton label="Close" onClick={onClose}>
						<X size={16} aria-hidden />
					</LightboxButton>
				</span>
			</div>

			{/* w-max lets a zoomed-in image widen the scroll area; m-auto centres
			    it while it is smaller, without clipping its left edge once larger. */}
			<div className="flex min-h-full w-max min-w-full px-4 pb-4 pt-14">
				<img
					src={src}
					alt={filename || ""}
					onClick={(event) => event.stopPropagation()}
					style={{ width }}
					className="m-auto block h-auto max-w-none cursor-default rounded-lg bg-white shadow-pop"
				/>
			</div>
		</div>,
		document.body
	)
}

function LightboxButton({ label, children, ...rest }) {
	return (
		<button
			type="button"
			className="inline-grid h-8 w-8 place-items-center rounded-lg bg-white/10 text-white transition-colors hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-white/10"
			aria-label={label}
			title={label}
			{...rest}
		>
			{children}
		</button>
	)
}
